import type { Quote } from "@/types/quote";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
	FileText,
	Plus,
	Trash2,
	Copy,
	ChevronRight,
	Clock,
	CheckCircle2,
	XCircle,
	Send,
	AlertCircle,
} from "lucide-react";

interface QuoteListProps {
	quotes: Quote[];
	onSelect: (id: string) => void;
	onDelete: (id: string) => void;
	onDuplicate: (id: string) => void;
	onNewQuote: () => void;
}

function formatCurrency(amount: number, currency: string) {
	return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount);
}

function getQuoteTotal(quote: Quote) {
	let total = 0;
	for (const item of quote.items) {
		const lineTotal = item.quantity * item.unitPrice;
		const discountAmt = lineTotal * (item.discount / 100);
		const afterDiscount = lineTotal - discountAmt;
		const taxAmt = afterDiscount * (item.tax / 100);
		total += afterDiscount + taxAmt;
	}
	return total;
}

const statusConfig: Record<string, { icon: React.ComponentType<{ className?: string }>; color: string; bg: string }> = {
	draft: { icon: Clock, color: "text-zinc-400", bg: "bg-zinc-700/50" },
	sent: { icon: Send, color: "text-blue-400", bg: "bg-blue-500/15" },
	accepted: { icon: CheckCircle2, color: "text-emerald-400", bg: "bg-emerald-500/15" },
	declined: { icon: XCircle, color: "text-red-400", bg: "bg-red-500/15" },
	expired: { icon: AlertCircle, color: "text-amber-400", bg: "bg-amber-500/15" },
};

export function QuoteList({ quotes, onSelect, onDelete, onDuplicate, onNewQuote }: QuoteListProps) {
	if (quotes.length === 0) return null;

	return (
		<div className="min-h-screen p-8">
			<div className="max-w-4xl mx-auto">
				<div className="flex items-center justify-between mb-8">
					<div>
						<div className="flex items-center gap-2 mb-1">
							<div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center">
								<FileText className="w-3.5 h-3.5 text-white" />
							</div>
							<span className="text-base font-semibold tracking-tight text-zinc-100">
								QuoteCraft
							</span>
						</div>
						<p className="text-sm text-zinc-500 ml-9">
							{quotes.length} proposal{quotes.length !== 1 ? "s" : ""}
						</p>
					</div>
					<Button onClick={onNewQuote} className="bg-blue-600 hover:bg-blue-500 text-white">
						<Plus className="w-4 h-4 mr-1.5" />
						New Proposal
					</Button>
				</div>

				<div className="space-y-3">
					{quotes.map((quote) => {
						const total = getQuoteTotal(quote);
						const status = (statusConfig[quote.status] ?? statusConfig.draft)!;
						const StatusIcon = status.icon;

						return (
							<Card
								key={quote.id}
								className="border-zinc-700/50 shadow-none hover:border-zinc-600 hover:shadow-lg hover:shadow-black/20 transition-all cursor-pointer group bg-zinc-800/50"
								onClick={() => onSelect(quote.id)}
							>
								<CardContent className="p-5">
									<div className="flex items-center justify-between">
										<div className="flex items-center gap-4 flex-1 min-w-0">
											<div
												className={`w-9 h-9 rounded-lg ${status.bg} flex items-center justify-center shrink-0`}
											>
												<StatusIcon className={`w-4 h-4 ${status.color}`} />
											</div>
											<div className="min-w-0 flex-1">
												<div className="flex items-center gap-2 mb-0.5">
													<h3 className="text-sm font-medium text-zinc-100 truncate">
														{quote.title}
													</h3>
													<Badge
														variant="secondary"
														className={`text-[10px] font-normal capitalize shrink-0 ${status.color} ${status.bg}`}
													>
														{quote.status}
													</Badge>
												</div>
												<div className="flex items-center gap-3 text-xs text-zinc-500">
													<span className="font-mono">{quote.quoteNumber}</span>
													<span>
														{quote.client.name || quote.client.company || "No client"}
													</span>
													<span>{quote.date}</span>
												</div>
											</div>
										</div>

										<div className="flex items-center gap-4">
											<span className="text-base font-semibold text-zinc-100 font-mono">
												{formatCurrency(total, quote.currency)}
											</span>
											<div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
												<button
													onClick={(e) => {
														e.stopPropagation();
														onDuplicate(quote.id);
													}}
													className="p-1.5 text-zinc-500 hover:text-zinc-300 rounded-md hover:bg-zinc-700 transition-colors"
												>
													<Copy className="w-3.5 h-3.5" />
												</button>
												<button
													onClick={(e) => {
														e.stopPropagation();
														onDelete(quote.id);
													}}
													className="p-1.5 text-zinc-500 hover:text-red-400 rounded-md hover:bg-red-500/10 transition-colors"
												>
													<Trash2 className="w-3.5 h-3.5" />
												</button>
											</div>
											<ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-zinc-400 transition-colors" />
										</div>
									</div>
								</CardContent>
							</Card>
						);
					})}
				</div>
			</div>
		</div>
	);
}
